import { z } from 'zod';
import { isoDateSchema, isoDateTimeSchema, pageQuerySchema, uuidSchema } from './common.js';
import { extractedDocumentTypeSchema } from './extraction.js';
import { reviewReasonSchema } from './review.js';
/**
 * The bookkeeping contract.
 *
 * An approved review becomes an accountant-ready record. QuickBooks stays the
 * system of record; nothing here is a general ledger, and nothing leaves
 * without a person.
 */
export const TRANSACTION_STATUSES = [
    'draft',
    'pending_review',
    'confirmed',
    'voided',
    'superseded',
];
export const transactionStatusSchema = z.enum(TRANSACTION_STATUSES);
/**
 * The brief's six states, mapped onto what exists.
 *
 * v0.3 wrote here that "Posted" and "Exported" are the same event because
 * QuickBooks is the system of record. **ADR 0017 reverses that premise:** this
 * ledger is the record, and QuickBooks is a publication target. A confirmed
 * record therefore *is* posted — to the books that count — which is why the
 * label below changed and the enum did not.
 *
 * A separate `posted` status is still not added. `confirmed` already carries
 * that meaning, and a third state would give two columns responsibility for
 * one fact.
 */
export const TRANSACTION_STATUS_LABELS = {
    draft: 'Draft',
    pending_review: 'Pending review',
    confirmed: 'Posted',
    voided: 'Voided',
    superseded: 'Replaced by a correction',
};
export const EXPORT_STATES = ['not_exported', 'queued', 'exported', 'stale', 'failed'];
export const exportStateSchema = z.enum(EXPORT_STATES);
/**
 * Who benefited.
 *
 * The question an accountant answers before choosing an account, because
 * "what category is this?" has no answer until "who was it for?" has one. The
 * same $4.99 is an expense, a draw, a benefit or a promotion depending only on
 * this, and the ledger honours it over any rule.
 */
export const BENEFICIARY_KINDS = [
    'business',
    'job',
    'owner',
    'employee',
    'customer',
    'asset',
];
export const beneficiaryKindSchema = z.enum(BENEFICIARY_KINDS);
export const BENEFICIARY_LABELS = {
    business: 'The business',
    job: 'A job',
    owner: 'The owner personally',
    employee: 'An employee',
    customer: 'A customer',
    asset: 'Something the business now owns',
};
/** What each choice means for the books, in the words a person would use. */
export const BENEFICIARY_CONSEQUENCES = {
    business: 'An operating expense. The credit is claimed.',
    job: 'Cost of goods sold, costed to the job. The credit is claimed.',
    owner: 'Due to/from the owner. Not an expense, not deductible, no credit claimed.',
    employee: 'A reimbursement or a taxable benefit. Someone decides which.',
    customer: 'Promotional, warranty or goodwill.',
    asset: 'Capitalised and depreciated over its life, not expensed this year.',
};
export const EXPENSE_CLASSIFICATIONS = [
    'operating_expense',
    'cost_of_goods_sold',
    'capital_asset',
    'inventory',
    'owner_draw',
    'personal',
    'non_deductible',
    'not_applicable',
];
export const expenseClassificationSchema = z.enum(EXPENSE_CLASSIFICATIONS);
export const EXPENSE_CLASSIFICATION_LABELS = {
    operating_expense: 'Operating expense',
    cost_of_goods_sold: 'Cost of goods sold',
    capital_asset: 'Capital asset',
    inventory: 'Inventory',
    owner_draw: 'Owner draw',
    personal: 'Personal',
    non_deductible: 'Not deductible',
    not_applicable: 'Not applicable',
};
/* -------------------------------------------------------------------------- */
/* Records                                                                    */
/* -------------------------------------------------------------------------- */
export const SETTLEMENTS = ['paid', 'on_account'];
export const settlementSchema = z.enum(SETTLEMENTS);
export const SETTLEMENT_LABELS = {
    paid: 'Paid when bought',
    on_account: 'On account, to be paid later',
};
/** Where a due date came from, so the list never presents a guess as a fact. */
export const DUE_ON_SOURCES = ['document', 'terms', 'assumed', 'person'];
export const dueOnSourceSchema = z.enum(DUE_ON_SOURCES);
export const DUE_ON_SOURCE_LABELS = {
    document: 'printed on the document',
    terms: 'from the printed terms',
    assumed: 'assumed at net 30',
    person: 'set by a person',
};
export const bookkeepingRecordSchema = z.object({
    id: uuidSchema,
    organizationId: uuidSchema,
    transactionType: z.string(),
    status: transactionStatusSchema,
    transactionDate: z.string(),
    /** Minor units as a string: a bigint does not survive JSON as a number. */
    grossAmountMinor: z.string(),
    netAmountMinor: z.string(),
    taxAmountMinor: z.string(),
    currencyCode: z.string(),
    reference: z.string().nullable(),
    exportState: exportStateSchema,
    postedAt: isoDateTimeSchema.nullable(),
    sourceDocumentId: uuidSchema.nullable(),
    documentFilename: z.string().nullable(),
    documentType: extractedDocumentTypeSchema.nullable(),
    vendorId: uuidSchema.nullable(),
    vendorName: z.string().nullable(),
    customerId: uuidSchema.nullable(),
    customerName: z.string().nullable(),
    categories: z.string().nullable(),
    lineCount: z.number().int(),
    linkedLineCount: z.number().int(),
    hasCapitalLine: z.boolean().nullable(),
    /** Who made the purchase, when it was a field purchase by a named person. */
    spentByTechnicianId: uuidSchema.nullable(),
    spentByName: z.string().nullable(),
    /** What still needs a person, so the list filters without a second query. */
    openReasons: z.array(reviewReasonSchema),
    createdAt: isoDateTimeSchema,
    /**
     * Paid when the document was issued, or owed on the supplier's account
     * until a bank line matched to the record pays it. Decided from the
     * document's payment block; a person can overrule it while the record is
     * a draft.
     */
    settlement: settlementSchema,
    dueOn: isoDateSchema.nullable(),
    dueOnSource: dueOnSourceSchema.nullable(),
    termsNetDays: z.number().int().nullable(),
    /** Money out the bank has matched to this record so far, in minor units. */
    settledMinor: z.string(),
    /** Why a voided or superseded record is gone. Null while it stands. */
    voidReason: z.string().nullable(),
    voidedAt: isoDateTimeSchema.nullable(),
    /** The signed record this one was copied from to correct it. */
    correctsRecordId: uuidSchema.nullable(),
    /** The draft copy that replaced this record. */
    supersededByRecordId: uuidSchema.nullable(),
});
/** Voiding and correcting both carry a reason that stays on the record. */
export const recordReasonRequestSchema = z.object({
    reason: z.string().trim().min(5, 'Say why, in a few words.').max(500),
});
/**
 * A part of the company with its own profit and loss.
 *
 * Optional by design. A shop that runs one trade has no divisions and never
 * sees the concept; a company running heating, plumbing and electrical needs
 * to know which of the three is actually making money.
 */
export const divisionSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    name: z.string(),
    trade: z.string().nullable(),
    isActive: z.boolean(),
});
export const bookkeepingLineSchema = z.object({
    id: uuidSchema,
    lineNumber: z.number().int(),
    description: z.string().nullable(),
    categoryId: uuidSchema,
    categoryName: z.string().nullable(),
    classification: expenseClassificationSchema,
    beneficiary: beneficiaryKindSchema,
    /** Why it was attributed that way, in one sentence a reviewer can read later. */
    beneficiaryReason: z.string().nullable(),
    divisionId: uuidSchema.nullable(),
    divisionCode: z.string().nullable(),
    ccaClassId: uuidSchema.nullable(),
    jobId: uuidSchema.nullable(),
    jobTitle: z.string().nullable(),
    propertyId: uuidSchema.nullable(),
    equipmentId: uuidSchema.nullable(),
    /** The vehicle a fuel, repair or insurance line belongs to. */
    vehicleId: uuidSchema.nullable(),
    jobLinkedAutomatically: z.boolean(),
    grossAmountMinor: z.string(),
    netAmountMinor: z.string(),
    taxAmountMinor: z.string(),
    itcEligiblePercent: z.number(),
});
export const bookkeepingRecordDetailSchema = bookkeepingRecordSchema.extend({
    lines: z.array(bookkeepingLineSchema),
    /**
     * How this record came to say what it says. Every value the engine chose is
     * a retraceable lookup, and this is where a bookkeeper reads the trail.
     */
    provenance: z.object({
        reviewDecisionId: uuidSchema.nullable(),
        approvedByName: z.string().nullable(),
        approvedAt: isoDateTimeSchema.nullable(),
        matchedVendorAliasId: uuidSchema.nullable(),
        correctionCount: z.number().int(),
    }),
});
/* -------------------------------------------------------------------------- */
/* Queue and search                                                           */
/* -------------------------------------------------------------------------- */
export const BOOKKEEPING_BUCKETS = [
    'needs_vendor',
    'needs_categorization',
    'needs_job',
    'needs_tax_review',
    'ready_for_export',
    'exported',
];
export const bookkeepingBucketSchema = z.enum(BOOKKEEPING_BUCKETS);
export const BOOKKEEPING_BUCKET_LABELS = {
    needs_vendor: 'Needs a supplier',
    needs_categorization: 'Needs a category',
    needs_job: 'Needs a job',
    needs_tax_review: 'Needs tax review',
    ready_for_export: 'Ready for export',
    exported: 'Exported',
};
export const listBookkeepingQuerySchema = pageQuerySchema.extend({
    bucket: bookkeepingBucketSchema.optional(),
    status: transactionStatusSchema.optional(),
    vendorId: uuidSchema.optional(),
    customerId: uuidSchema.optional(),
    jobId: uuidSchema.optional(),
    categoryId: uuidSchema.optional(),
    /** Matches supplier, reference, invoice number and filename. */
    query: z.string().trim().min(1).max(200).optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    minAmountMinor: z.coerce.number().int().optional(),
    maxAmountMinor: z.coerce.number().int().optional(),
});
/* -------------------------------------------------------------------------- */
/* Writing                                                                    */
/* -------------------------------------------------------------------------- */
/**
 * Editing a record.
 *
 * Only the fields a bookkeeper decides. Amounts are absent on purpose: they
 * came from a document a human already confirmed, and changing them here would
 * make the record disagree with the paper behind it. A wrong amount is fixed
 * by re-reviewing the document or by an adjustment record.
 *
 * Strict, unlike every other request schema in this file, and deliberately so:
 * the default behaviour would strip an unknown `grossAmountMinor` and return
 * 204, leaving the caller believing they changed a total that never moved.
 * Absent-and-refused is a guarantee; absent-and-ignored is a trap.
 */
export const updateRecordRequestSchema = z.strictObject({
    vendorId: uuidSchema.nullable().optional(),
    customerId: uuidSchema.nullable().optional(),
    reference: z.string().trim().max(120).nullable().optional(),
    /** Who made the purchase. Not who uploaded the paper. */
    spentByTechnicianId: uuidSchema.nullable().optional(),
    /**
     * Paid, or owed on the supplier's account. Switching to on account moves
     * the record's credit to Accounts payable when it is signed; the bank
     * line that pays it later moves it to the bank.
     */
    settlement: settlementSchema.optional(),
    /** Only read when the record is on account. Null leaves the engine's date. */
    dueOn: isoDateSchema.nullable().optional(),
    lines: z
        .array(z.object({
        id: uuidSchema,
        categoryId: uuidSchema.optional(),
        classification: expenseClassificationSchema.optional(),
        beneficiary: beneficiaryKindSchema.optional(),
        beneficiaryReason: z.string().trim().max(500).nullable().optional(),
        divisionId: uuidSchema.nullable().optional(),
        ccaClassId: uuidSchema.nullable().optional(),
        jobId: uuidSchema.nullable().optional(),
        propertyId: uuidSchema.nullable().optional(),
        equipmentId: uuidSchema.nullable().optional(),
        vehicleId: uuidSchema.nullable().optional(),
        itcEligiblePercent: z.number().min(0).max(100).optional(),
        description: z.string().trim().max(500).nullable().optional(),
    }))
        .max(200)
        .optional(),
    /** Why, when a bookkeeper overrides what the engine proposed. */
    reason: z.string().trim().min(3).max(500).optional(),
});
export const confirmRecordRequestSchema = z.object({
    notes: z.string().trim().max(2000).optional(),
});
/* -------------------------------------------------------------------------- */
/* Many at once                                                               */
/* -------------------------------------------------------------------------- */
/** One decision applied to a run of drafts. A key present as null clears the field. */
export const applyToRecordsRequestSchema = z.strictObject({
    recordIds: z.array(uuidSchema).min(1).max(1000),
    patch: z
        .strictObject({
        vendorId: uuidSchema.nullable().optional(),
        spentByTechnicianId: uuidSchema.nullable().optional(),
        categoryId: uuidSchema.optional(),
        jobId: uuidSchema.nullable().optional(),
        divisionId: uuidSchema.nullable().optional(),
        vehicleId: uuidSchema.nullable().optional(),
        beneficiary: beneficiaryKindSchema.optional(),
    })
        .refine((patch) => Object.keys(patch).length > 0, 'Nothing to apply.'),
});
export const applyToRecordsResultSchema = z.object({
    updated: z.number().int(),
    skipped: z.array(z.object({ id: uuidSchema, reason: z.string() })),
});
export const confirmManyRequestSchema = z.object({
    recordIds: z.array(uuidSchema).min(1).max(1000),
});
/** The last signed record from the same supplier, as a template. */
export const lastTimeSchema = z.object({
    transactionId: uuidSchema,
    transactionDate: z.string(),
    grossAmountMinor: z.string(),
    spentByTechnicianId: uuidSchema.nullable(),
    settlement: z.string().nullable(),
    lines: z.array(z.object({
        lineNumber: z.number().int(),
        categoryId: uuidSchema,
        categoryName: z.string(),
        classification: expenseClassificationSchema,
        beneficiary: beneficiaryKindSchema,
        divisionId: uuidSchema.nullable(),
        ccaClassId: uuidSchema.nullable(),
        vehicleId: uuidSchema.nullable(),
        netAmountMinor: z.string(),
    })),
    /** Of the supplier's signed records, how many were coded to the same set of accounts. */
    codedTheSame: z.number().int(),
    of: z.number().int(),
});
export const bookkeepingSettingsSchema = z.object({
    capitalThresholdMinor: z.string(),
    largeAmountThresholdMinor: z.string(),
    requireVendor: z.boolean(),
    requireJobLink: z.boolean(),
});
/**
 * Everything a record editor needs to offer a choice.
 *
 * One payload rather than four endpoints: a bookkeeper opens a record and has
 * to be able to pick a supplier, a category and a job immediately, and three
 * more round trips is three more chances for the screen to be half-usable.
 */
export const bookkeepingReferenceSchema = z.object({
    vendors: z.array(z.object({
        id: uuidSchema,
        displayName: z.string(),
        defaultCategoryId: uuidSchema.nullable(),
    })),
    categories: z.array(z.object({
        id: uuidSchema,
        code: z.string().nullable(),
        name: z.string(),
        accountType: z.string(),
        defaultClassification: expenseClassificationSchema,
    })),
    jobs: z.array(z.object({
        id: uuidSchema,
        jobNumber: z.string().nullable(),
        title: z.string(),
        customerName: z.string().nullable(),
    })),
    divisions: z.array(divisionSchema),
    /** Field staff, for saying who actually made a purchase. */
    employees: z.array(z.object({ id: uuidSchema, fullName: z.string(), employeeNumber: z.string().nullable() })),
    vehicles: z.array(z.object({ id: uuidSchema, name: z.string(), plate: z.string().nullable() })),
    ccaClasses: z.array(z.object({
        id: uuidSchema,
        classCode: z.string(),
        description: z.string(),
    })),
});
export const bookkeepingStatsSchema = z.object({
    draft: z.number().int(),
    confirmed: z.number().int(),
    readyForExport: z.number().int(),
    needsVendor: z.number().int(),
    needsCategorization: z.number().int(),
    needsTaxReview: z.number().int(),
    capitalRecommendations: z.number().int(),
    /** How long a record sits between being built and being confirmed. */
    medianSecondsToConfirm: z.number().nullable(),
    confirmedToday: z.number().int(),
    /** How often a bookkeeper disagrees with the engine's proposal. */
    categoryOverrideRate: z.number().nullable(),
    vendorOverrideRate: z.number().nullable(),
});
/** Which queue bucket a record belongs to, derived once for both sides. */
export function bucketFor(record) {
    if (record.exportState === 'exported')
        return 'exported';
    if (record.openReasons.includes('needs_vendor_link'))
        return 'needs_vendor';
    if (record.openReasons.includes('needs_categorization'))
        return 'needs_categorization';
    if (record.openReasons.includes('tax_ambiguous'))
        return 'needs_tax_review';
    if (record.openReasons.includes('needs_job_link'))
        return 'needs_job';
    return 'ready_for_export';
}
/** Minor units to a readable amount. Never via a float. */
export function formatMinor(minor, currency = 'CAD') {
    const value = BigInt(minor);
    const negative = value < 0n;
    const absolute = negative ? -value : value;
    const dollars = absolute / 100n;
    const cents = absolute % 100n;
    const body = `${dollars.toLocaleString('en-CA')}.${cents.toString().padStart(2, '0')}`;
    const symbol = currency === 'CAD' || currency === 'USD' ? '$' : '';
    return negative ? `(${symbol}${body})` : `${symbol}${body}`;
}
//# sourceMappingURL=bookkeeping.js.map